import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { Dashboard } from '@uppy/react';
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';
import { getFileType, getFilePreviewUrl } from './patrolUtils';

export const FilterDialog = ({ open, onClose, filterIncidents, setFilterIncidents, filterPatrols, setFilterPatrols }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs">
    <DialogTitle>Filter</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Show Only:
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={filterIncidents}
              onChange={(e) => setFilterIncidents(e.target.checked)}
              sx={{ color: '#f50057', '&.Mui-checked': { color: '#f50057' } }}
            />
          }
          label="Incidents"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={filterPatrols}
              onChange={(e) => setFilterPatrols(e.target.checked)}
              sx={{ color: '#f50057', '&.Mui-checked': { color: '#f50057' } }}
            />
          }
          label="Patrols"
        />
      </FormGroup>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">Back</Button>
    </DialogActions>
  </Dialog>
);

export const EmailDialog = ({
  open,
  onClose,
  emailChips,
  emailInput,
  setEmailInput,
  onEmailKeyDown,
  onDeleteChip,
  onSend,
  isSending,
  isLoadingEmails,
}) => {
  const theme = useTheme();

  return (
    <Dialog open={open} onClose={() => !isSending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Email Notification</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Note: Press Enter key once you are done typing an email.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Email
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            p: 1,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            mt: 0.5,
          }}
        >
          {isLoadingEmails ? (
            <CircularProgress size={20} />
          ) : (
            <>
              {emailChips.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  onDelete={isSending ? undefined : () => onDeleteChip(email)}
                  size="small"
                  sx={{ bgcolor: 'primary.main', color: 'white' }}
                />
              ))}
              <TextField
                variant="standard"
                placeholder="user@domain.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={onEmailKeyDown}
                InputProps={{ disableUnderline: true }}
                sx={{ flex: 1, minWidth: 150 }}
                disabled={isSending}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={isSending}>Cancel</Button>
        <Button
          onClick={onSend}
          color="primary"
          variant="contained"
          disabled={emailChips.length === 0 || isSending}
          startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSending ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const FileViewerDialog = ({ open, onClose, file, onDownload }) => {
  if (!file) return null;
  const fileType = getFileType(file);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" noWrap sx={{ maxWidth: '80%' }}>{file.name}</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, bgcolor: 'grey.900' }}>
        {fileType === 'image' ? (
          <Box
            component="img"
            src={getFilePreviewUrl(file)}
            alt={file.name}
            sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
          />
        ) : fileType === 'video' ? (
          <Box
            component="video"
            src={getFilePreviewUrl(file)}
            controls
            autoPlay
            sx={{ maxWidth: '100%', maxHeight: '70vh' }}
          />
        ) : (
          <Typography color="grey.400">Preview not available for this file type</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onDownload(file)} startIcon={<DownloadIcon />}>Download</Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export const DeleteConfirmDialog = ({ open, onClose, file, onConfirm, isDeleting }) => (
  <Dialog open={open} onClose={() => !isDeleting && onClose()} maxWidth="xs">
    <DialogTitle>Delete Attachment</DialogTitle>
    <DialogContent>
      <Typography>
        Are you sure you want to delete <strong>{file?.name}</strong>?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        This action cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={isDeleting}>Cancel</Button>
      <Button
        onClick={onConfirm}
        color="error"
        variant="contained"
        disabled={isDeleting}
        startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
      >
        {isDeleting ? 'Deleting...' : 'Delete'}
      </Button>
    </DialogActions>
  </Dialog>
);

export const UploadDialog = ({ open, onClose, uppy, theme }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUploadIcon color="primary" />
        <Typography variant="h6">Upload Attachments</Typography>
      </Box>
      <IconButton onClick={onClose}><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent>
      {uppy && (
        <Dashboard
          uppy={uppy}
          proudlyDisplayPoweredByUppy={false}
          showProgressDetails
          height={400}
          theme={theme.palette.mode === 'dark' ? 'dark' : 'light'}
        />
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);
